import { api } from '../../client';

export interface TaskTrackerSprintDto {
  id: string;
  project_id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  sort_order: number;
}

export interface TaskTrackerColumnDto {
  id: string;
  project_id: string;
  name: string;
  sort_order: number;
}

export interface TaskTrackerItemDto {
  id: string;
  sprint_id: string;
  column_id: string;
  title: string;
  description: string | null;
  sort_order: number;
  planning_item_id?: string | null;
  assigned_employee_id?: string | null;
  assigned_employee_name?: string | null;
}

export const projectsTaskTrackerApi = {
  // Sprints
  listSprints(projectId: string): Promise<{ data?: TaskTrackerSprintDto[]; message?: string }> {
    return api.get(
      `/projects/task-tracker/sprints?project_id=${encodeURIComponent(projectId)}`
    ) as Promise<{ data: TaskTrackerSprintDto[] }>;
  },
  createSprint(
    projectId: string,
    data: { name: string; start_date?: string | null; end_date?: string | null; sort_order?: number }
  ): Promise<{ data?: TaskTrackerSprintDto; message?: string }> {
    return api.post('/projects/task-tracker/sprints', { project_id: projectId, ...data }) as Promise<{
      data: TaskTrackerSprintDto;
    }>;
  },
  updateSprint(
    _projectId: string,
    sprintId: string,
    data: { name?: string; start_date?: string | null; end_date?: string | null; sort_order?: number }
  ): Promise<{ data?: TaskTrackerSprintDto; message?: string }> {
    return api.put(`/projects/task-tracker/sprints/${sprintId}`, data) as Promise<{
      data: TaskTrackerSprintDto;
      message?: string;
    }>;
  },
  deleteSprint(_projectId: string, sprintId: string): Promise<{ success?: boolean; message?: string }> {
    return api.delete(`/projects/task-tracker/sprints/${sprintId}`) as Promise<{
      success?: boolean;
      message?: string;
    }>;
  },
  /** Move all tasks in the sprint that are not in the Done column to the project's Backlog sprint. */
  moveSprintIncompleteToBacklog(
    _projectId: string,
    sprintId: string
  ): Promise<{ success?: boolean; moved?: number; message?: string }> {
    return api.post(`/projects/task-tracker/sprints/${sprintId}/move-incomplete-to-backlog`, {}) as Promise<{
      success: boolean;
      moved: number;
      message?: string;
    }>;
  },

  // Columns
  listColumns(projectId: string): Promise<{ data?: TaskTrackerColumnDto[]; message?: string }> {
    return api.get(
      `/projects/task-tracker/columns?project_id=${encodeURIComponent(projectId)}`
    ) as Promise<{ data: TaskTrackerColumnDto[] }>;
  },
  createColumn(
    projectId: string,
    data: { name: string; sort_order?: number }
  ): Promise<{ data?: TaskTrackerColumnDto; message?: string }> {
    return api.post('/projects/task-tracker/columns', { project_id: projectId, ...data }) as Promise<{
      data: TaskTrackerColumnDto;
    }>;
  },
  updateColumn(
    _projectId: string,
    columnId: string,
    data: { name?: string; sort_order?: number }
  ): Promise<{ data?: TaskTrackerColumnDto; message?: string }> {
    return api.put(`/projects/task-tracker/columns/${columnId}`, data) as Promise<{
      data: TaskTrackerColumnDto;
      message?: string;
    }>;
  },
  deleteColumn(_projectId: string, columnId: string): Promise<{ success?: boolean; message?: string }> {
    return api.delete(`/projects/task-tracker/columns/${columnId}`) as Promise<{
      success?: boolean;
      message?: string;
    }>;
  },

  // Tasks (items)
  listItems(projectId: string): Promise<{ data?: TaskTrackerItemDto[]; message?: string }> {
    return api.get(
      `/projects/task-tracker/items?project_id=${encodeURIComponent(projectId)}`
    ) as Promise<{ data: TaskTrackerItemDto[] }>;
  },
  createItem(
    projectId: string,
    data: {
      sprint_id: string;
      column_id: string;
      title: string;
      description?: string | null;
      sort_order?: number;
      planning_item_id?: string | null;
      assigned_employee_id?: string | null;
    }
  ): Promise<{ data?: TaskTrackerItemDto; message?: string }> {
    return api.post('/projects/task-tracker/items', { project_id: projectId, ...data }) as Promise<{
      data: TaskTrackerItemDto;
    }>;
  },
  updateItem(
    _projectId: string,
    itemId: string,
    data: {
      sprint_id?: string;
      column_id?: string;
      title?: string;
      description?: string | null;
      sort_order?: number;
      planning_item_id?: string | null;
      assigned_employee_id?: string | null;
    }
  ): Promise<{ data?: TaskTrackerItemDto; message?: string }> {
    return api.put(`/projects/task-tracker/items/${itemId}`, data) as Promise<{
      data: TaskTrackerItemDto;
      message?: string;
    }>;
  },
  deleteItem(_projectId: string, itemId: string): Promise<{ success?: boolean; message?: string }> {
    return api.delete(`/projects/task-tracker/items/${itemId}`) as Promise<{
      success?: boolean;
      message?: string;
    }>;
  },
};
